import React, { useEffect, useState } from "react";
import {
  discardShowcaseItem,
  getHeroShowcase,
} from "../../utils/showcaseClientCache";
import { markImageFailed } from "../../utils/imageProbe";

/**
 * Full-bleed shopping atmosphere for the hero.
 * Only products with a proven-loadable image are assigned to tiles.
 */
const SLOT_LAYOUT = [
  { id: 1, x: 56, y: 16, w: 118, h: 142, hue: "teal", delay: 0 },
  { id: 2, x: 74, y: 12, w: 102, h: 128, hue: "coral", delay: 0.8 },
  { id: 3, x: 90, y: 22, w: 96, h: 120, hue: "gold", delay: 1.5 },
  { id: 4, x: 60, y: 48, w: 124, h: 110, hue: "mint", delay: 0.4 },
  { id: 5, x: 79, y: 44, w: 108, h: 132, hue: "teal", delay: 1.2 },
  { id: 6, x: 93, y: 52, w: 92, h: 116, hue: "coral", delay: 2.0 },
  { id: 7, x: 66, y: 76, w: 112, h: 128, hue: "gold", delay: 1.7 },
  { id: 8, x: 85, y: 78, w: 104, h: 122, hue: "mint", delay: 2.4 },
];

const TilePhoto = ({ product, eager, onDead }) => {
  const images = product?.images || [];
  const [index, setIndex] = useState(0);
  const src = images[index] || null;

  if (!src) {
    return (
      <>
        <span className="lp-shop-scene__tile-bar" />
        <span className="lp-shop-scene__tile-bar lp-shop-scene__tile-bar--short" />
      </>
    );
  }

  return (
    <img
      className="lp-shop-scene__photo"
      src={src}
      alt=""
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => {
        markImageFailed(src);
        if (index + 1 < images.length) {
          setIndex(index + 1);
          return;
        }
        if (onDead) onDead(product);
      }}
    />
  );
};

const HeroShopScene = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getHeroShowcase(SLOT_LAYOUT.length)
      .then((items) => {
        if (!cancelled) setProducts(items);
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dropProduct = (product) => {
    if (!product?.id) return;
    discardShowcaseItem(product);
    setProducts((prev) => prev.filter((p) => p.id !== product.id));
  };

  const tiles = SLOT_LAYOUT.map((slot, i) => ({
    ...slot,
    product: products[i] || null,
    z: 2 + (i % 4),
  }));

  return (
    <div className="lp-shop-scene" aria-hidden="true">
      <div className="lp-shop-scene__aurora" />
      <div className="lp-shop-scene__grid" />

      <svg
        className="lp-shop-scene__orbit"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient id="lp-orbit-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#069cb4" stopOpacity="0.55" />
            <stop offset="50%" stopColor="#fcd66a" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#f94c4b" stopOpacity="0.45" />
          </linearGradient>
          <linearGradient id="lp-link-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#069cb4" stopOpacity="0" />
            <stop offset="40%" stopColor="#069cb4" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#f94c4b" stopOpacity="0" />
          </linearGradient>
        </defs>

        <ellipse
          className="lp-shop-scene__ring lp-shop-scene__ring--a"
          cx="78"
          cy="48"
          rx="28"
          ry="18"
          fill="none"
          stroke="url(#lp-orbit-grad)"
          strokeWidth="0.15"
        />
        <ellipse
          className="lp-shop-scene__ring lp-shop-scene__ring--b"
          cx="78"
          cy="48"
          rx="38"
          ry="24"
          fill="none"
          stroke="url(#lp-orbit-grad)"
          strokeWidth="0.12"
        />
        <ellipse
          className="lp-shop-scene__ring lp-shop-scene__ring--c"
          cx="78"
          cy="48"
          rx="48"
          ry="30"
          fill="none"
          stroke="url(#lp-orbit-grad)"
          strokeWidth="0.08"
        />

        <g
          className="lp-shop-scene__links"
          stroke="url(#lp-link-grad)"
          strokeWidth="0.2"
          fill="none"
        >
          <path className="lp-shop-scene__link" d="M62 24 L74 46 L86 28" />
          <path
            className="lp-shop-scene__link lp-shop-scene__link--slow"
            d="M68 68 L78 48 L90 56"
          />
          <path className="lp-shop-scene__link" d="M58 48 L72 40 L84 70" />
        </g>
      </svg>

      <div className="lp-shop-scene__field">
        {tiles.map((tile, i) => {
          const hasPhoto = !!tile.product?.images?.length;
          return (
            <div
              key={tile.id}
              className={
                "lp-shop-scene__tile lp-shop-scene__tile--" +
                tile.hue +
                (hasPhoto ? " lp-shop-scene__tile--photo" : "")
              }
              style={{
                left: `${tile.x}%`,
                top: `${tile.y}%`,
                width: tile.w,
                height: tile.h,
                zIndex: tile.z,
                animationDelay: `${tile.delay}s`,
              }}
            >
              <div className="lp-shop-scene__tile-inner">
                <TilePhoto
                  product={tile.product}
                  eager={i < 2}
                  onDead={dropProduct}
                />
              </div>
              <span className="lp-shop-scene__tile-shine" />
            </div>
          );
        })}
      </div>

      <div className="lp-shop-scene__sparkle" />
      <div className="lp-shop-scene__sparkle lp-shop-scene__sparkle--b" />
    </div>
  );
};

export default HeroShopScene;
